<template>
  <el-dialog :title="title" :visible="visible" width="600px" @update:visible="$emit('close')" custom-class="with-title">
    <div class="tw-benchmark-tag-assign">
      <p v-if="mode === 'bulk'" class="tw-benchmark-tag-assign__note">
        選択中の {{ targetCount }} 件の投稿にタグを設定します（選んだタグで全置換されます）。
      </p>
      <p class="tw-benchmark-tag-assign__count">選択中のタグ {{ selectedTagIds.length }} / {{ TAG_PER_POST_MAX }}</p>
      <el-checkbox-group :value="selectedTagIds" class="tw-benchmark-tag-assign__group" @input="onChange">
        <el-checkbox
          v-for="tag in tags"
          :key="tag.id"
          :label="tag.id"
          :disabled="isTagDisabled(tag.id)"
          class="tw-benchmark-tag-assign__item"
        >
          {{ tag.name }}
        </el-checkbox>
      </el-checkbox-group>
      <p v-if="!tags.length" class="tw-benchmark-tag-assign__empty">タグがありません。タグ管理から作成してください。</p>
    </div>
    <template slot="footer">
      <el-button size="small" @click="onCancel">キャンセル</el-button>
      <el-button size="small" type="primary" @click="onSave">保存</el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from 'vue'

import { TrackingService } from '@/client/services'
import { TAG_PER_POST_MAX } from '@/typings/twitter_benchmark_campaign_posts'

import { useTwBenchmarkTagsStore } from '../../store/useTwBenchmarkTagsStore'

type AssignMode = 'single' | 'bulk'

/**
 * タグ付与ダイアログ（単体 / 一括 共通）。保存で { mode, tagIds } を emit するだけで、全置換 API 呼び出しは親が担う。
 */
export default defineComponent({
  name: 'TwBenchmarkTagAssignDialog',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String as PropType<AssignMode>,
      default: 'single'
    },
    initialTagIds: {
      type: Array as PropType<number[]>,
      default: () => []
    },
    targetCount: {
      type: Number,
      default: 0
    }
  },

  emits: ['close', 'save'],

  setup(props, { emit }) {
    const tagsStore = useTwBenchmarkTagsStore()
    const selectedTagIds = ref<number[]>([])

    watch(
      () => props.visible,
      isOpen => {
        if (isOpen) {
          selectedTagIds.value = props.mode === 'single' ? [...props.initialTagIds] : []
        }
      },
      { immediate: true }
    )

    const onChange = (ids: number[]): void => {
      selectedTagIds.value = ids
    }

    const onSave = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|タグ付与|保存')
      emit('save', { mode: props.mode, tagIds: [...selectedTagIds.value] })
      emit('close')
    }

    const onCancel = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|タグ付与|キャンセル')
      emit('close')
    }

    const isTagDisabled = (tagId: number): boolean =>
      selectedTagIds.value.length >= TAG_PER_POST_MAX && !selectedTagIds.value.includes(tagId)

    return {
      TAG_PER_POST_MAX,
      tags: computed(() => tagsStore.tags),
      selectedTagIds,
      isTagDisabled,
      title: computed(() => (props.mode === 'single' ? 'タグを付与' : '選択した投稿にタグ付与')),
      onChange,
      onSave,
      onCancel
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

:deep(.el-dialog__footer) {
  border-top: 1px solid $border-color;
}

.tw-benchmark-tag-assign {
  max-height: 60vh;
  overflow-y: auto;

  &__note {
    margin: 0 0 $spacing-unit * 2;
    font-size: $font-small-size;
    color: $sub-text-color;
  }

  &__count {
    margin: 0 0 $spacing-unit;
    font-size: $font-small-size;
    color: $sub-text-color;
  }

  &__group {
    display: block;
  }

  &__item {
    display: flex;
    align-items: center;
    width: 100%;
    height: auto;
    padding: $spacing-unit;
    margin: 0;
    box-shadow: none;
  }

  &__empty {
    padding: $spacing-unit * 2;
    color: $sub-text-color;
    text-align: center;
  }
}
</style>
